import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Check, Plus, Twitter } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { DEFAULT_LANDING_ROUTE } from '../../routes/destinations';
import { LoginModal } from '../Login/Login';
import RegisterModal from '../Register/RegisterModal';
import ProductImage from '../../assets/product_2026.png';
import PlayerVoteBreakdownImage from '../../assets/player_comparison_2026.png';
import PlayerComparisonImage1 from '../../assets/comparison_2026_1.png';
import PlayerComparisonImage2 from '../../assets/comparison_2_2026.png';
import { trackButtonClick, trackLinkClick } from '../../utils/analytics';

const SEASON = 2026;

const NAV_LINKS = [
  { id: 'about', label: 'About' },
  { id: 'data', label: 'Our Data' },
  { id: 'pricing', label: 'Free Access' },
  { id: 'faq', label: 'FAQ' },
];

const MARKETS = [
  'Player Finishes: Top 3, Top 5, Top 10, Top 20',
  'Player Votes: X+ Votes, Player Vote Lines',
  'Team Vote Winners & Team Totals',
  'Player Comparisons: H2H, Trios, Groups',
  '3‑Vote Games',
  'BYO Brownlow',
  'Leader After Round X',
  'Games Polled',
  'Player Handicaps',
  '…and more!',
];

const PLAN_FEATURES = [
  'Player Leaderboard & Vote Summary',
  'Player & Team Vote Breakdowns',
  'Team Vote Winners & Team Totals',
  'Player Vote Comparisons',
  'Game Votes',
  'BYO Brownlow Tool',
];

const FAQS = [
  {
    id: 'mobile',
    q: 'Does Brownlow Genius work on mobile?',
    a: (
      <p>
        Brownlow Genius works on both desktop and mobile, but for the best user experience we recommend using the
        desktop version.
      </p>
    ),
  },
  {
    id: 'lock',
    q: 'Is there such thing as a Brownlow lock?',
    a: (
      <>
        <p className="mb-3">
          In sports betting, there is no such thing as a "lock" and all bets carry risk. This is especially true for
          Brownlow betting due to the unpredictable nature of the voting. Umpires can make mistakes, and over the years
          we've seen some howlers that are simply not reasonable to predict. Therefore, there is no such thing as a
          "lock" Brownlow bet. You must gamble responsibly and use betting strategies to minimize your risk/exposure
          when betting on the Brownlow to give yourself the best chance to win.
        </p>
        <p>
          While our platform provides a wide range of vote data to use, it's impossible to predict with 100% accuracy
          how the umpires will vote in a game. That said, we don't need to be perfect—we just need to be more right
          than the bookies.
        </p>
      </>
    ),
  },
];

/* ------------------------------------------------------------------------ */
/* Primitives                                                                */
/* ------------------------------------------------------------------------ */

// Brand gradient for headline emphasis — stays inside the emerald ramp.
const Grad = ({ children }) => (
  <span className="bg-gradient-to-r from-emerald-400 via-emerald-300 to-emerald-200 bg-clip-text text-transparent">
    {children}
  </span>
);

const Eyebrow = ({ children }) => (
  <div className="text-[11px] font-mono font-semibold uppercase tracking-[0.22em] text-emerald-400/90">{children}</div>
);

// Hairline section divider: fades out at both ends so sections read as one
// continuous page rather than stacked boxes.
const Divider = () => (
  <div aria-hidden="true" className="mx-auto h-px max-w-6xl bg-gradient-to-r from-transparent via-slate-700/50 to-transparent" />
);

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
};

const PRIMARY_CTA_CLASS =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 text-slate-950 font-semibold shadow-[0_0_0_1px_rgba(16,185,129,0.35),0_12px_32px_-12px_rgba(16,185,129,0.55)] transition-all hover:bg-emerald-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#020817]';


// Product screenshot: shown as-is, just rounded with a hairline ring and a
// deep shadow so it sits naturally on the dark page.
const Screenshot = ({ src, alt, className = '', imgClassName = '', loading }) => (
  <div className={`relative h-full ${className}`}>
    <img
      src={src}
      alt={alt}
      loading={loading}
      className={`block w-full rounded-xl ring-1 ring-white/[0.08] shadow-2xl shadow-black/50 ${imgClassName}`}
    />
  </div>
);

// macOS-style window chrome for current-theme captures (no color correction
// needed — the screenshot already matches the site palette).
const MacWindow = ({ src, alt, title, className = '' }) => (
  <div
    className={`overflow-hidden rounded-xl border border-white/[0.08] bg-[#0b1220] shadow-2xl shadow-black/60 ${className}`}
  >
    <div className="relative flex h-9 items-center border-b border-white/[0.06] bg-white/[0.03] px-4">
      <div className="flex items-center gap-2" aria-hidden="true">
        <span className="h-3 w-3 rounded-full bg-[#ff5f57] ring-1 ring-inset ring-black/20" />
        <span className="h-3 w-3 rounded-full bg-[#febc2e] ring-1 ring-inset ring-black/20" />
        <span className="h-3 w-3 rounded-full bg-[#28c840] ring-1 ring-inset ring-black/20" />
      </div>
      {title && (
        <span className="pointer-events-none absolute left-1/2 hidden max-w-[60%] -translate-x-1/2 truncate rounded-md border border-white/[0.06] bg-[#020817]/60 px-3 py-0.5 text-[11px] font-mono text-slate-500 sm:block">
          {title}
        </span>
      )}
    </div>
    <img src={src} alt={alt} className="block w-full" />
  </div>
);

/* ------------------------------------------------------------------------ */
/* Header                                                                    */
/* ------------------------------------------------------------------------ */

const LandingNav = ({ isAuthenticated, onLoginClick, onRegisterClick }) => (
  <header className="sticky top-0 z-40 border-b border-white/[0.05] bg-[#020817]/70 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-6">
      <Link
        to={isAuthenticated ? DEFAULT_LANDING_ROUTE : '/'}
        className="flex items-center gap-2.5"
        aria-label="Brownlow Genius home"
      >
        <img src="/favicon.svg" alt="" className="h-7 w-7 object-contain" />
        <span className="text-[15px] font-semibold tracking-tight text-white">Brownlow Genius</span>
      </Link>

      <nav className="hidden items-center gap-1 md:flex" aria-label="Page sections">
        {NAV_LINKS.map((l) => (
          <button
            key={l.id}
            type="button"
            onClick={() => scrollToSection(l.id)}
            className="rounded-lg px-3 py-1.5 text-[13px] font-medium text-slate-400 transition-colors hover:bg-white/[0.04] hover:text-white"
          >
            {l.label}
          </button>
        ))}
      </nav>

      <div className="flex items-center gap-1.5">
        {!isAuthenticated && (
          <button
            type="button"
            onClick={() => { trackLinkClick('Log in', 'Navbar'); onLoginClick(); }}
            className="inline-flex rounded-lg px-3 py-2 text-[13px] font-medium text-slate-300 transition-colors hover:bg-white/[0.04] hover:text-white"
          >
            Log in
          </button>
        )}
        {isAuthenticated ? (
          <Link
            to="/dashboard"
            onClick={() => trackButtonClick('Open Dashboard', 'Navbar')}
            className={`${PRIMARY_CTA_CLASS} rounded-lg px-3.5 py-2 text-[13px] shadow-none hover:translate-y-0`}
          >
            Open Dashboard
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => { trackButtonClick('Create Free Account', 'Navbar'); onRegisterClick(); }}
            className={`${PRIMARY_CTA_CLASS} hidden rounded-lg px-3.5 py-2 text-[13px] shadow-none hover:translate-y-0 sm:inline-flex`}
          >
            Create Free Account
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  </header>
);

/* ------------------------------------------------------------------------ */
/* Hero                                                                      */
/* ------------------------------------------------------------------------ */

const Hero = ({ isAuthenticated, onRegisterClick }) => (
  <section className="relative overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute left-1/2 top-0 h-[620px] w-[1100px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/[0.08] blur-[180px]" />
    </div>

    <div className="relative mx-auto max-w-6xl px-5 pb-12 pt-16 text-center sm:px-6 md:pb-16 md:pt-24">
      <h1 className="mx-auto max-w-4xl text-[2.6rem] font-bold leading-[1.04] tracking-[-0.03em] text-white sm:text-5xl md:text-6xl lg:text-7xl">
        <Grad>Brownlow Genius.</Grad>
        <br />
        Australia's #1
        <br className="hidden sm:block" /> Brownlow Resource
      </h1>

      <p className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-slate-400 md:text-xl">
        Providing you with all the data and tools you need to analyse and navigate the{' '}
        <span className="whitespace-nowrap font-medium text-slate-200">{SEASON} AFL Brownlow Medal</span>.
      </p>

      <div className="mt-10 flex flex-col items-center gap-3.5">
        {isAuthenticated ? (
          <Link
            to="/dashboard"
            onClick={() => trackButtonClick('Open Dashboard', 'Hero Section')}
            className={`${PRIMARY_CTA_CLASS} w-full px-8 py-4 text-base sm:w-auto`}
          >
            Open Dashboard
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => { trackButtonClick('Create Free Account', 'Hero Section'); onRegisterClick(); }}
            className={`${PRIMARY_CTA_CLASS} w-full px-8 py-4 text-base sm:w-auto`}
          >
            Create Free Account
            <ArrowRight className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>

    {/* Product preview framed as a Mac window */}
    <div className="relative mx-auto max-w-6xl px-5 pb-16 sm:px-6 md:pb-24">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-16 top-6 h-56 rounded-full bg-emerald-500/[0.12] blur-3xl" />
      <MacWindow
        src={ProductImage}
        alt="Brownlow Genius dashboard preview"
        title="brownlowgenius.com/dashboard"
        className="relative"
      />
    </div>
  </section>
);

/* ------------------------------------------------------------------------ */
/* About                                                                     */
/* ------------------------------------------------------------------------ */

const About = () => (
  <section id="about" className="scroll-mt-20">
    <div className="mx-auto max-w-6xl px-5 py-14 sm:px-6 md:py-20">
      <div className="grid items-start gap-12 lg:grid-cols-12 lg:gap-16 lg:items-center">
        <div className="lg:col-span-7">
          <Eyebrow>About</Eyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-[-0.02em] text-white sm:text-4xl md:text-5xl">
            What is <Grad>Brownlow Genius?</Grad>
          </h2>

          <div className="mt-7 space-y-5 text-base leading-relaxed text-slate-400 md:text-[17px]">
            <p>
              Brownlow Genius is a platform built for those who love a punt on the Brownlow. It visualises and breaks
              down vote data in a way that helps punters analyse, research, and make more informed betting decisions on
              the AFL Brownlow.
            </p>
            <p>
              Using our vote data, Brownlow Genius offers a wide range of tools that let users explore and cover a
              variety of markets. These include:
            </p>
          </div>

          <div className="mt-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 md:p-6">
            <div className="mb-4">
              <h3 className="text-[11px] font-mono font-semibold uppercase tracking-[0.18em] text-slate-400">
                Markets covered
              </h3>
            </div>
            <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {MARKETS.map((m) => (
                <li key={m} className="flex items-start gap-2.5 text-[14px] leading-snug text-slate-300">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.5} aria-hidden="true" />
                  <span>{m}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-7 text-base leading-relaxed text-slate-400 md:text-[17px]">
            Brownlow Genius makes it simple to visualise these markets across every count in our dataset—putting the
            data right at your fingertips.
          </p>
        </div>

        <div className="lg:col-span-5">
          <div className="relative mx-auto max-w-[22rem]">
            <div aria-hidden="true" className="pointer-events-none absolute inset-8 rounded-full bg-emerald-500/[0.12] blur-3xl" />
            <Screenshot
              src={PlayerVoteBreakdownImage}
              alt="Player vote breakdown"
              loading="lazy"
              className="relative"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------------ */
/* Our Data                                                                  */
/* ------------------------------------------------------------------------ */

const STATS = [
  { value: '5', label: 'Internal counts' },
  { value: '3', label: 'External counts' },
  { value: '8', label: 'Counts combined' },
];

const Data = () => {
  return (
    <section id="data" className="scroll-mt-20">
      <div className="mx-auto max-w-6xl px-5 py-14 sm:px-6 md:py-20">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="relative order-last lg:order-first lg:col-span-6">
            <div aria-hidden="true" className="pointer-events-none absolute inset-6 rounded-full bg-emerald-500/[0.12] blur-3xl" />
            <div className="relative space-y-4">
              <Screenshot
                src={PlayerComparisonImage1}
                alt="Player comparison table"
                loading="lazy"
              />
              <Screenshot
                src={PlayerComparisonImage2}
                alt="Player comparison vote progression chart"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-6">
            <Eyebrow>Our Data</Eyebrow>
            <h2 className="mt-3 text-3xl font-bold tracking-[-0.02em] text-white sm:text-4xl md:text-5xl">
              Brownlow Genius <Grad>Voting</Grad>
            </h2>
            <div className="mt-7 space-y-5 text-base leading-relaxed text-slate-400 md:text-[17px]">
              <p>
                Behind the Brownlow Genius vote data is a private group of five people who live and breathe the
                Brownlow. Each member has been betting on the Brownlow and tracking their own counts for years—some for
                over a decade with great success. Over this time, each member has developed their own unique process for
                assigning votes in every game.
              </p>
              <p>
                Throughout the season, each member watches as many games as possible and assigns weighted votes to the
                players they believe are most likely to poll. The team then collates this data (five internal counts)
                and combines it with three external counts to produce a weighted average. The result is a balanced,
                data-driven picture of where we think the count is most likely to land on Brownlow night.
              </p>
            </div>

            <dl className="mt-8 grid grid-cols-3 divide-x divide-white/[0.06] border-y border-white/[0.06] py-5">
              {STATS.map((s) => (
                <div key={s.label} className="px-4 text-center first:pl-0 last:pr-0">
                  <dd className="text-3xl font-bold tabular-nums tracking-tight text-white md:text-4xl">{s.value}</dd>
                  <dt className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.14em] text-slate-500">{s.label}</dt>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------------ */
/* Pricing                                                                   */
/* ------------------------------------------------------------------------ */

const Pricing = ({ isAuthenticated, onRegisterClick }) => (
  <section id="pricing" className="scroll-mt-20">
    <div className="mx-auto max-w-6xl px-5 py-14 sm:px-6 md:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="mt-3 text-3xl font-bold tracking-[-0.02em] sm:text-4xl md:text-5xl">
          <Grad>Free Access</Grad>
        </h2>
        <p className="mt-4 text-base text-slate-400 md:text-[17px]">
          Complete access to Brownlow Genius for the {SEASON} AFL Season - completely free.
        </p>
      </div>

      <div className="relative mx-auto mt-12 max-w-md">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-8 -top-8 h-40 rounded-full bg-emerald-500/[0.14] blur-3xl" />
        <div className="relative rounded-2xl border border-white/[0.08] bg-[#0f172a]/70 p-7 shadow-2xl shadow-black/50 backdrop-blur-sm md:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-mono font-semibold uppercase tracking-[0.18em] text-slate-400">
                {SEASON} Brownlow Genius
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-5xl font-bold tracking-tight text-white">FREE</span>
                <span className="text-sm text-slate-500">/ season</span>
              </div>
            </div>
          </div>

          {isAuthenticated ? (
            <Link
              to="/dashboard"
              onClick={() => trackButtonClick('Open Dashboard', 'Pricing Section')}
              className={`${PRIMARY_CTA_CLASS} mt-7 w-full px-6 py-3.5 text-[15px]`}
            >
              Open Dashboard
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => { trackButtonClick('Create Free Account', 'Pricing Section'); onRegisterClick(); }}
              className={`${PRIMARY_CTA_CLASS} mt-7 w-full px-6 py-3.5 text-[15px]`}
            >
              Create Free Account
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} aria-hidden="true" />
            </button>
          )}

          <ul className="mt-7 space-y-3 border-t border-white/[0.06] pt-7">
            {PLAN_FEATURES.map((f) => (
              <li key={f} className="flex items-center gap-3 text-[14px] text-slate-300">
                <Check className="h-4 w-4 shrink-0 text-emerald-400" strokeWidth={2.5} aria-hidden="true" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------------ */
/* FAQ                                                                       */
/* ------------------------------------------------------------------------ */

const FAQ = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <section id="faq" className="scroll-mt-20">
      <div className="mx-auto max-w-3xl px-5 py-14 sm:px-6 md:py-20">
        <div className="text-center">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-[-0.02em] text-white sm:text-4xl md:text-5xl">Got Questions?</h2>
          <p className="mt-4 text-base text-slate-400 md:text-[17px]">Everything you need to know about Brownlow Genius.</p>
        </div>

        <div className="mt-12 divide-y divide-white/[0.06] border-y border-white/[0.06]">
          {FAQS.map(({ id, q, a }) => {
            const open = openFaq === id;
            return (
              <div key={id}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(open ? null : id)}
                  aria-expanded={open}
                  aria-controls={`faq-${id}`}
                  className="group flex w-full items-center justify-between gap-6 py-5 text-left"
                >
                  <span className={`text-[15px] font-medium transition-colors md:text-base ${open ? 'text-white' : 'text-slate-200 group-hover:text-white'}`}>
                    {q}
                  </span>
                  <Plus
                    className={`h-4 w-4 shrink-0 transition-all ${open ? 'rotate-45 text-emerald-400' : 'text-slate-500 group-hover:text-slate-300'}`}
                    strokeWidth={2.5}
                    aria-hidden="true"
                  />
                </button>
                {open && (
                  <div id={`faq-${id}`} className="pb-6 pr-10 text-[14px] leading-relaxed text-slate-400">
                    {a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

/* ------------------------------------------------------------------------ */
/* Footer                                                                    */
/* ------------------------------------------------------------------------ */

const Footer = () => (
  <footer className="border-t border-white/[0.05]">
    <div className="mx-auto max-w-6xl px-5 sm:px-6">
      <div className="flex flex-col items-center gap-6 py-10 text-center md:flex-row md:items-center md:justify-between md:text-left">
        <div className="flex items-center gap-3">
          <img src="/favicon.svg" alt="" className="h-6 w-6 object-contain" />
          <span className="text-sm font-semibold tracking-tight text-white">Brownlow Genius</span>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] md:justify-end">
          <Link to="/terms" className="text-slate-400 transition-colors hover:text-white">
            Terms of Service
          </Link>
          <Link to="/privacy" className="text-slate-400 transition-colors hover:text-white">
            Privacy Policy
          </Link>
          <a
            href="https://x.com/brownlow_genius"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackLinkClick('Twitter', 'Footer')}
            title="Follow us on X"
            aria-label="Follow Brownlow Genius on X"
            className="text-slate-400 transition-colors hover:text-white"
          >
            <Twitter className="h-4 w-4" aria-hidden="true" />
          </a>
          <span className="text-slate-600">© Brownlow Genius</span>
        </div>
      </div>

      <div className="border-t border-white/[0.05] py-5">
        <p className="text-center text-[13px] leading-relaxed text-slate-500">
          Always remember to gamble responsibly. For free and confidential support call{' '}
          <a href="tel:1800858858" className="font-medium text-slate-400 transition-colors hover:text-white">1800 858 858</a>
          {' '}or visit{' '}
          <a
            href="https://www.gamblinghelponline.org.au"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackLinkClick('Gambling Help Online', 'Footer')}
            className="font-medium text-slate-400 transition-colors hover:text-white"
          >
            gamblinghelponline.org.au
          </a>.
        </p>
      </div>
    </div>
  </footer>
);

/* ------------------------------------------------------------------------ */

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const openLogin = useCallback(() => { setShowRegister(false); setShowLogin(true); }, []);
  const closeLogin = useCallback(() => setShowLogin(false), []);
  const openRegister = useCallback(() => { setShowLogin(false); setShowRegister(true); }, []);
  const closeRegister = useCallback(() => setShowRegister(false), []);

  // Auto-open the login modal when arriving from a protected-route redirect
  // (redirectTo in state) or after a session-expired bounce.
  useEffect(() => {
    if (isAuthenticated) return;
    const hasRedirect = location.state?.redirectTo;
    const hasExpiry = sessionStorage.getItem('sessionExpired') === 'true';
    if (hasRedirect || hasExpiry) {
      setShowLogin(true);
    }
  }, [location.state, isAuthenticated]);

  return (
    <div className="min-h-screen bg-[#020817] text-white antialiased">
      <LandingNav isAuthenticated={isAuthenticated} onLoginClick={openLogin} onRegisterClick={openRegister} />
      <main>
        <Hero isAuthenticated={isAuthenticated} onRegisterClick={openRegister} />
        <About />
        <Divider />
        <Data />
        <Divider />
        <Pricing isAuthenticated={isAuthenticated} onRegisterClick={openRegister} />
        <Divider />
        <FAQ />
      </main>
      <Footer />
      {showLogin && <LoginModal onClose={closeLogin} onSwitchToRegister={openRegister} />}
      {showRegister && <RegisterModal onClose={closeRegister} onSwitchToLogin={openLogin} />}
    </div>
  );
};

export default LandingPage;
